import React, { createContext, useContext, useEffect } from 'react';

interface PlausibleContextType {
  trackEvent: (eventName: string, props?: { props?: Record<string, string | number> }) => void;
}

const PlausibleContext = createContext<PlausibleContextType>({
  trackEvent: () => {},
});

interface PlausibleProviderProps {
  children: React.ReactNode;
  domain: string;
  trackOutboundLinks?: boolean;
  trackFileDownloads?: boolean;
  apiHost?: string;
}

const PlausibleProvider: React.FC<PlausibleProviderProps> = ({ 
  children, 
  domain,
  trackOutboundLinks = true,
  trackFileDownloads = true,
  apiHost = 'https://plausible.io'
}) => {
  useEffect(() => {
    // Skip in development if desired
    // if (process.env.NODE_ENV === 'development') return;

    // Load the Plausible script
    const scriptId = 'plausible-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.defer = true;
      script.setAttribute('data-domain', domain);
      
      // Add optional attributes
      if (trackOutboundLinks) {
        script.setAttribute('data-outbound-links', 'true');
      }
      
      if (trackFileDownloads) {
        script.setAttribute('data-file-downloads', 'true');
      }
      
      script.src = `${apiHost}/js/script.js`;
      document.head.appendChild(script);
    }
  }, [domain, trackOutboundLinks, trackFileDownloads, apiHost]);

  // Function to track custom events
  const trackEvent = (eventName: string, props?: { props?: Record<string, string | number> }) => {
    if (window.plausible) {
      window.plausible(eventName, props);
    }
  };

  return (
    <PlausibleContext.Provider value={{ trackEvent }}>
      {children}
    </PlausibleContext.Provider>
  );
};

// Custom hook to use Plausible
export const usePlausible = () => {
  const { trackEvent } = useContext(PlausibleContext);
  return trackEvent;
};

// Add type definition for window.plausible
declare global {
  interface Window {
    plausible?: (eventName: string, props?: any) => void;
  }
}

export default PlausibleProvider; 